// Price stickers: small stickers printed on a Brother label printer's continuous 62 mm roll. Each PDF page is one piece of the
// roll: 62 mm wide and one sticker tall, holding one sticker (1 column) or two side by side (2 columns).

export const ROLL_WIDTH_MM = 62;
export const PADDING_MM = 3;   // the text always stays at least 3 mm from every edge of its sticker (the Brother printer's safe zone)
export const DPI = 600;        // pages are drawn at 600 dots per inch (the printer prints at 300), so edges stay crisp
export const PX_PER_MM = DPI / 25.4;
export const PT_PER_MM = 72 / 25.4;

export type Columns = 1 | 2;

// The sticker heights on offer (the roll is cut to this length for every page).
export const HEIGHTS = [
  { key: '13mm', label: '1.3 cm', mm: 13 },
  { key: '20mm', label: '2 cm', mm: 20 },
  { key: '1in', label: '1 inch', mm: 25.4 },
  { key: '25mm', label: '2.5 cm', mm: 25 },
  { key: '30mm', label: '3 cm', mm: 30 },
  { key: '2in', label: '2 inches', mm: 50.8 },
  { key: '50mm', label: '5 cm', mm: 50 },
] as const;
export type HeightKey = (typeof HEIGHTS)[number]['key'];
export const heightMm = (key: HeightKey) => HEIGHTS.find((h) => h.key === key)!.mm;

// The fonts on offer. Contempora Sans comes from our Adobe Fonts kit (like the rest of Label Studio); the others are part of the
// app itself (@fontsource packages), so they always load. `css` is what the drawing code asks the browser for.
export const FONTS = [
  { key: 'contempora', label: 'Contempora Sans Condensed (our brand font)', family: 'contempora-sans-condensed', weight: 400 },
  { key: 'bebas', label: 'Bebas Neue (tall capitals)', family: 'Bebas Neue', weight: 400 },
  { key: 'oswald', label: 'Oswald SemiBold', family: 'Oswald', weight: 600 },
  { key: 'barlow', label: 'Barlow Condensed Bold', family: 'Barlow Condensed', weight: 700 },
  { key: 'roboto', label: 'Roboto Condensed Bold', family: 'Roboto Condensed', weight: 700 },
  { key: 'opensans', label: 'Open Sans Bold (wide)', family: 'Open Sans Variable', weight: 700 },
] as const;
export type FontKey = (typeof FONTS)[number]['key'];
export const fontOf = (key: FontKey) => FONTS.find((f) => f.key === key)!;
export const fontCss = (key: FontKey, px: number) => { const f = fontOf(key); return `${f.weight} ${px}px "${f.family}", sans-serif`; };

export interface StickerSettings {
  columns: Columns;
  height: HeightKey;
  font: FontKey;
  autoSize: boolean;   // true: the size that fills the sticker's height (3 mm margin); a sticker with too much text gets smaller
  sizePt: number;      // used when autoSize is off
  spacingPt: number;   // letter spacing, in points (can be negative)
  cutLine: boolean;    // a very light dotted line between the two columns, to cut along
}

export const DEFAULT_SETTINGS: StickerSettings = { columns: 1, height: '13mm', font: 'contempora', autoSize: true, sizePt: 24, spacingPt: 0, cutLine: true };
